import React, {useEffect, useMemo, useState} from "react";
import {PaginaLogada} from "../../componentes";
import {ActionIcon, Badge, Center, Grid, Group, Modal, Switch, TextInput, Tooltip} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import {BotaoCadastrar, BotaoFechar, BotaoSalvar} from "../../componentes/botao/botao-mantine";
import {useForm} from "@mantine/form";
import {COL_RESP_6, COR, ICONE} from "../../config/constantes/constantes-mantine";
import {exibirMensagemSucesso} from "../../util";
import {Delegacia} from "../../tipos/delegacia";
import DelegaciaServico from "../../servicos/delegacia-servico";
import {DelegaciaCmd} from "../../tipos/cmd/delegacia-cmd";
import SelectFetchMantine from "../../componentes/form/select-fetch-mantine";
import {MantineReactTable, MRT_ColumnDef, useMantineReactTable} from "mantine-react-table";
import {MRT_Localization_PT_BR} from "mantine-react-table/locales/pt-BR";

export default function Delegacias() {

  const [modal, {open, close}] = useDisclosure(false);
  const [isCarregando, setIsCarregando] = useState(false)
  const [flagAlterar, setFlagAlterar] = useState(false)
  const [lista, setLista] = useState<Delegacia[]>([])
  const form = useForm<DelegaciaCmd>({
    validate: {
      nome: (value) => (value.length <= 4 ? "Nome muito curto" : null),
      email: (value) => (/^\S+@\S+$/.test(value!!) ? null : 'Email inválido'),
    },
  });

  const colunas = useMemo<MRT_ColumnDef<Delegacia>[]>(() => [
      {
        accessorKey: 'nome',
        header: 'Nome',
      },
      {
        accessorKey: 'area.nome',
        header: 'Área',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
      },
      {
        accessorKey: 'ativa',
        header: 'Status',
        Cell: ({cell}) => (
          <Badge color={cell.getValue() ? COR.PRIMARIO : COR.PERIGO}>
            {cell.getValue() ? "Ativa" : "Inativa"}
          </Badge>
        )
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns: colunas,
    data: lista,
    autoResetPageIndex: false,
    enableSorting: false,
    localization: MRT_Localization_PT_BR,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({row}) => (
      <Group wrap="nowrap" gap="lg">
        <Tooltip label={"Editar"}>
          <ActionIcon
            aria-label="Editar"
            color={COR.ALERTA}
            onClick={() => alterarDelegacia(row.original)}
          >
            <ICONE.EDITAR/>
          </ActionIcon>
        </Tooltip>
      </Group>
    ),
  })

  const buscarDelegacias = async () => {
    setIsCarregando(true)
    const result = await DelegaciaServico.listarDelegacias();
    setLista(result)
    setIsCarregando(false)
  }

  function salvarDelegacia() {
    setIsCarregando(true)
    if (flagAlterar) {
      DelegaciaServico.alterarDelegacia(form.values)
        .then(r => r.json())
        .then(json => {
          buscarDelegacias()
          exibirMensagemSucesso("Delegacia alterada com sucesso")
        })
    } else {
      DelegaciaServico.salvarDelegacia(form.values)
        .then(r => r.json())
        .then(json => {
          lista.push(json)
          setLista(lista)
          exibirMensagemSucesso("Delegacia salva com Sucesso")
        })
    }
      setIsCarregando(false)
      form.reset()
      close()
  }

  function alterarDelegacia(f: Delegacia) {
    setFlagAlterar(true)
    form.setValues(DelegaciaCmd.paraCmd(f))
    open()
  }

  useEffect(() => {
    buscarDelegacias();
  }, [])

  return (
    <PaginaLogada titulo="Delegacia" isCarregando={isCarregando}>

      <BotaoCadastrar onClick={open} mb={30} />

      <MantineReactTable table={table}/>

      <Modal opened={modal} onClose={() => {close(); setFlagAlterar(false)}} size={"lg"} title={flagAlterar ? "Alterar Delegacia" : "Cadastrar Delegacia"}>
        <form onSubmit={form.onSubmit(salvarDelegacia)}>
          <Grid>
            <Grid.Col span={COL_RESP_6}>
              <TextInput
                required
                label={"Nome"}
                placeholder={"Ex. Delagacia Plantonista"}
                {...form.getInputProps('nome')}
                onChange={(e) => form.setFieldValue("nome", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <SelectFetchMantine
                label={"Área:"}
                url={"/area"}
                placeholder={"Selecione a Área"}
                value={form.values.areaId}
                onChange={(e) => {
                  form.setFieldValue("areaId", e!!)
                }}/>
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <TextInput
               label={"Email"}
               placeholder={"Email"}
               {...form.getInputProps('email')}
               onChange={(e) => form.setFieldValue("email", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <TextInput
                label={"Telefone"}
                placeholder={"Telefone"}
                {...form.getInputProps('telefone')}
                onChange={(e) => form.setFieldValue("telefone", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <Switch
                label={"Status"}
                onLabel="Ativa"
                offLabel="Inativa"
                size="lg"
                width={200}
                w={200}
                checked={form.getValues().ativa}
                {...form.getInputProps('ativa')}
                styles={{
                  track: {
                    width: 80,
                  },
                  trackLabel: {
                    fontSize: 12,
                    }
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} mt={20}>
              <Center>
                <BotaoSalvar type={"submit"} m={5}/>
                <BotaoFechar onClick={() => {close(); setFlagAlterar(false)}} m={5}/>
              </Center>
            </Grid.Col>
          </Grid>
        </form>
      </Modal>
    </PaginaLogada>
  );
}
